<script setup>
import { ref, watch } from 'vue';
import HeaderDialog from '@/components/widgets/creationDialog/HeaderDialog.vue';
import RetryFailedRequest from '@/components/widgets/RetryFailedRequest.vue';
import { useDisplay } from 'vuetify';
import { debounce } from 'lodash';
import { useI18n } from 'vue-i18n';
import TablePagination from '@/components/widgets/TablePagination.vue';

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false
    },
    title: {
        type: String
    },
    subTitle: {
        type: String
    },
    columns: [
        {
            key: String,
            sortable: Boolean,
            width: String
        }
    ],
    loadData: {
        type: Function,
        required: true
    },
    errorMessage: {
        type: String,
        default: ''
    },
    canExtractItems: {
        type: Boolean,
        default: false
    },
    entity: {
        type: Boolean,
        default: false
    },
    multiple: {
        type: Boolean,
        default: true
    },
    itemsPerPage: {
        type: Number,
        default: 10
    }
});

const { mdAndDown } = useDisplay();
const { t } = useI18n({ useScope: 'global' });

const emit = defineEmits(['update:modelValue', 'selected']);

// Data
const items = ref([]);
const selectedItems = ref(new Map());
const loading = ref(false);
const page = ref(1);
const pageSize = ref(props.itemsPerPage);
const totalItems = ref(0);
const searchPattern = ref('');
const localErrorMessage = ref(props.errorMessage);

// Computed
// const totalPages = computed(() => Math.ceil(totalItems.value / pageSize.value));

// Methods
const debouncedSearch = debounce((newSearch) => {
    searchPattern.value = newSearch;
    handleReloadData();
}, 300);

const handleReloadData = async () => {
    loading.value = true;
    const response = await props.loadData({
        page: page.value,
        limit: pageSize.value,
        pattern: searchPattern.value
    });
    items.value = response.data || [];
    totalItems.value = response.total || 0;
    loading.value = false;
};

watch(
    () => props.modelValue,
    async (newVal) => {
        if (newVal) {
            page.value = 1;
            selectedItems.value.clear();
            handleReloadData();
        }
    }
);

// Re-cargar cuando cambie page o pageSize (disparado por el footer nativo)
watch(page, () => handleReloadData());
watch(pageSize, () => {
    page.value = 1;
    handleReloadData();
});

const handleSearchChange = (search) => {
    debouncedSearch(search);
};

const handleDataSelection = (item, checked) => {
    if (!props.multiple && checked) {
        items.value.forEach((i) => {
            if (i !== item) {
                i.selected = false;
                selectedItems.value.delete(i.id);
            }
        });
    }

    if (checked) {
        selectedItems.value.set(item.id, item);
    } else {
        selectedItems.value.delete(item.id);
    }
    item.selected = checked;
};

const handleConfirm = () => {
    let selectedData = Array.from(selectedItems.value.values());
    if (selectedData.length > 0) {
        localErrorMessage.value = '';

        if (props.entity) {
            selectedData = selectedData.map((selectedItem) => {
                return items.value.find((el) => el.id === selectedItem.id);
            });
        } else if (props.canExtractItems) {
            selectedData = SelectedPiecesData(selectedData);
        }

        emit('selected', selectedData);
        emit('update:modelValue', false);
    } else {
        localErrorMessage.value = t('Select at least one item to send');
    }
};

const SelectedPiecesData = (selectedData) => {
    return selectedData.map((selectedItem) => {
        const item = items.value.find((el) => el.id === selectedItem.id);
        return {
            ...selectedItem,
            pieces:
                item?.selectedItems && parseInt(item.selectedItems) > 0
                    ? parseInt(item.selectedItems)
                    : parseInt(item.numberPack)
        };
    });
};

const handleClose = () => {
    selectedItems.value.clear();
    items.value = [];
    totalItems.value = 0;
    searchPattern.value = '';
    localErrorMessage.value = '';
    emit('update:modelValue', false);
};

const validateNumberPack = (item) => [
    (value) => value > 0 || 'Must be greater than 0',
    (value) => value <= item.numberPack || `Must be ${item.numberPack} or less`
];

const getNestedValue = (obj, key) => {
    return key.split('.').reduce((acc, part) => acc && acc[part], obj);
};

const updatePiecesInput = (item, value) => {
    if (
        parseInt(value.data) > parseInt(item.numberPack) ||
        parseInt(value.data) < 1
    ) {
        return;
    } else {
        items.value = items.value.map((el) =>
            el.id === item.id ? { ...el, selectedItems: value.data } : el
        );
        return value.data;
    }
};

// Watch for dialog open
watch(
    () => props.modelValue,
    async (newVal) => {
        if (newVal) {
            page.value = 1;
            selectedItems.value.clear();
            items.value = [];
            totalItems.value = 0;
            searchPattern.value = '';
            handleReloadData();
        } else {
            items.value = [];
            totalItems.value = 0;
            searchPattern.value = '';
            selectedItems.value.clear();
            localErrorMessage.value = '';
        }
    }
);
</script>

<template>
    <v-dialog
        :model-value="modelValue"
        @update:model-value="$emit('update:modelValue', $event)"
        max-width="1500px"
        transition="dialog-bottom-transition"
        scrollable
    >
        <v-card class="dialog-card">
            <v-card-title class="sticky-header">
                <HeaderDialog
                    class="mt-4"
                    :page="subTitle"
                    :title="title"
                    :isMobile="mdAndDown"
                    :hideIcons="true"
                    :isSearch="true"
                    :isSingle="true"
                    :show-retry-button="loading"
                    :save-button-text="$t('Confirm')"
                    @save="handleConfirm"
                    @close="handleClose"
                />
            </v-card-title>

            <v-card-text class="dialog-content">
                <RetryFailedRequest
                    v-if="localErrorMessage"
                    :message="localErrorMessage"
                    @retry="emit('retry')"
                />

                <v-text-field
                    v-model="searchPattern"
                    :label="$t('Search')"
                    prepend-inner-icon="mdi-magnify"
                    variant="outlined"
                    hide-details
                    single-line
                    density="compact"
                    class="mb-4"
                    @update:model-value="handleSearchChange"
                />

                <v-data-table-server
                    :headers="columns"
                    :items="items"
                    :loading="loading"
                    v-model:items-per-page="pageSize"
                    v-model:page="page"
                    :items-length="totalItems"
                    density="compact"
                    class="data-table-no-scroll"
                    hide-default-footer
                >
                    <template #item="{ item }">
                        <tr :class="{ 'selected-row': item.selected }">
                            <td class="checkbox-cell">
                                <input
                                    type="checkbox"
                                    :checked="item.selected"
                                    @change="
                                        handleDataSelection(
                                            item,
                                            $event.target.checked
                                        )
                                    "
                                    class="custom-checkbox"
                                />
                            </td>
                            <td
                                v-for="col in columns.filter(
                                    (c) => c.key !== 'select'
                                )"
                                :key="col.key"
                            >
                                <template
                                    v-if="
                                        col.key === 'numberPack' &&
                                        canExtractItems
                                    "
                                >
                                    <v-text-field
                                        v-model="item.numberPack"
                                        :value="item[col.key]"
                                        :placeholder="item.numberPack"
                                        :max="item.numberPack"
                                        type="number"
                                        variant="outlined"
                                        hide-details
                                        single-line
                                        density="compact"
                                        class="pieces-input custom-width"
                                        :rules="validateNumberPack(item)"
                                        @input="updatePiecesInput(item, $event)"
                                    />
                                </template>
                                <template v-else-if="col.type === 'boolean'">
                                    <v-icon
                                        v-if="getNestedValue(item, col.key)"
                                        color="green"
                                    >
                                        mdi-check
                                    </v-icon>
                                    <v-icon v-else color="red">
                                        mdi-close
                                    </v-icon>
                                </template>
                                <template v-else>
                                    {{ getNestedValue(item, col.key) }}
                                </template>
                            </td>
                        </tr>
                    </template>

                    <template #no-data>
                        <div class="d-flex justify-center pa-4">
                            {{ $t('No items available') }}
                        </div>
                    </template>
                </v-data-table-server>
            </v-card-text>

            <v-card-actions class="pagination-controls sticky-footer">
                <TablePagination
                    :page="page"
                    :page-size="pageSize"
                    :total-items="totalItems"
                    @update:page="page = $event"
                    @update:pageSize="pageSize = $event"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style lang="scss" scoped>
.dialog-card {
    display: flex;
    flex-direction: column;
    height: 80vh;
    max-height: 80vh;
}

.sticky-header {
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: white;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.dialog-content {
    flex: 1;
    overflow-y: auto;
    padding: 16px 24px;
}

.sticky-footer {
    position: sticky;
    bottom: 0;
    z-index: 10;
    background-color: white;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
    padding: 12px 24px;
    display: flex;
    align-items: center;
    gap: 16px;
}

.pagination-controls {
    justify-content: flex-end;
    padding-right: 32px;
    background: white;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.pagination-info {
    font-size: 0.95rem;
    color: rgba(0, 0, 0, 0.87);
    margin-left: 8px;
    margin-right: 8px;
    white-space: nowrap;
}

.items-per-page-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
}

.items-per-page-label {
    font-size: 0.95rem;
    color: rgba(0, 0, 0, 0.87);
    font-weight: 400;
    margin-right: 4px;
    white-space: nowrap;
}

.items-per-page-select {
    width: 90px;
    min-width: 90px;

    :deep(.v-field__input) {
        padding: 8px 12px;
        min-height: 40px;
    }

    :deep(.v-field) {
        min-height: 40px;
    }

    :deep(.v-select__selection-text) {
        font-size: 0.875rem;
        color: rgba(0, 0, 0, 0.87);
        font-weight: 400;
    }

    :deep(.v-field__outline) {
        color: rgba(0, 0, 0, 0.38);
    }
}

.data-table-no-scroll {
    :deep(.v-table__wrapper) {
        overflow-y: visible !important;
        max-height: none !important;
    }
}

::v-deep(.titleModal) {
    margin-left: 50px;
    flex-basis: auto;
}

.checkbox-cell {
    min-width: 40px;
    width: 40px;
    max-width: 48px;
    text-align: center;
    vertical-align: middle;
    background: transparent !important;
    padding: 0 !important;
}

:deep(.v-data-table thead th) {
    font-weight: bold !important;
}

:deep(.v-data-table .v-table__wrapper > table tbody > tr),
.selected-row {
    height: 32px !important;
    min-height: 32px !important;
    font-size: 0.95rem !important;
}

:deep(.v-data-table .v-table__wrapper > table td),
:deep(.v-data-table .v-table__wrapper > table th) {
    padding-top: 6px !important;
    padding-bottom: 6px !important;
    min-height: 28px !important;
    height: 32px !important;
}

.custom-checkbox {
    width: 18px !important;
    height: 18px !important;
    accent-color: #00686f;
    cursor: pointer;
    margin: 0 auto;
    display: block;
}

.custom-width {
    width: 70px;
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 4px;
}

.pagination-row {
    display: flex;
    align-items: center;
    gap: 16px;
}
</style>
